// ReadyWhenYouAre.jsx
import * as React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  useTheme,
} from "@mui/material";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";

import CtaButton from "./CtaButton";
import { CONSULTATION_COPY } from "../constants/consultation";

export default function ReadyWhenYouAre() {
  const theme = useTheme();
  const [values, setValues] = React.useState({
    name: "",
    email: "",
    meeting: "",
    referral: "",
    consent: false,
  });

  const handleChange = (field) => (e) => {
    const value = field === 'email' ? e.target.value.trimStart() : e.target.value;
    setValues((v) => ({ ...v, [field]: value }));
  };
  const handleCheck = (e) =>
    setValues((v) => ({ ...v, consent: e.target.checked }));

  const fieldSX = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#fff",
      borderRadius: 2,
      "& fieldset": {
        borderColor: "#D9CEC7",
      },
      "&:hover fieldset": {
        borderColor: "#2B3F53",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2B3F53",
        borderWidth: 2,
      },
    },
  };

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="md">
        {/* Header card */}
        <Paper
          elevation={0}
          sx={{
            px: { xs: 2.5, md: 3.5 },
            py: { xs: 2.5, md: 3 },
            mb: 2.5,
            borderRadius: 3,
            bgcolor: "#F3EEE7",
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Typography
            sx={{
              ...theme.typography.heading,
              fontSize: { xs: 24, md: 40 },
              textAlign: "left",
            }}
          >
            Ready When You Are
          </Typography>
          <Typography sx={{ ...theme.typography.subtext, textAlign: "left" }}>
            Your path is your own. When you're ready, we're here to meet you
            with presence and care. Please use the form below to get in touch.
          </Typography>
        </Paper>

        {/* Form */}
        <Box
          sx={{
            bgcolor: "#FAFAFA",
            borderRadius: 4,
            p: { xs: 3, sm: 4, md: 5 },
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                ...theme.typography.heading,
                fontSize: { xs: 24, sm: 28, md: 32 },
                fontWeight: 700,
                mb: 1,
                textAlign: "center",
              }}
            >
              {CONSULTATION_COPY.bookYour}
            </Typography>
            <Typography
              sx={{
                ...theme.typography.subtext,
                fontSize: { xs: 14, sm: 16 },
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              Fill out the form below and we'll match you with the right
              practitioner
            </Typography>
          </Box>

          {/* Row 1: Full Name and Email */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 3,
              mb: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  ...theme.typography.subtext,
                  fontSize: 14,
                  fontWeight: 600,
                  mb: 1,
                  color: "rgba(43,30,23,0.85)",
                }}
              >
                Full Name *
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your full name"
                value={values.name}
                onChange={handleChange("name")}
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonRoundedIcon sx={{ color: "rgba(43,30,23,0.4)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSX}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  ...theme.typography.subtext,
                  fontSize: 14,
                  fontWeight: 600,
                  mb: 1,
                  color: "rgba(43,30,23,0.85)",
                }}
              >
                Email Address *
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your email"
                value={values.email}
                onChange={handleChange("email")}
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon sx={{ color: "rgba(43,30,23,0.4)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSX}
              />
            </Box>
          </Box>

          {/* Row 2: Meeting Type and Referral */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 3,
              mb: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  ...theme.typography.subtext,
                  fontSize: 14,
                  fontWeight: 600,
                  mb: 1,
                  color: "rgba(43,30,23,0.85)",
                }}
              >
                Meeting Type
              </Typography>
              <TextField
                fullWidth
                value="Video Call (Online)"
                size="medium"
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScheduleRoundedIcon
                        sx={{ color: "rgba(43,30,23,0.4)" }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSX}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  ...theme.typography.subtext,
                  fontSize: 14,
                  fontWeight: 600,
                  mb: 1,
                  color: "rgba(43,30,23,0.85)",
                }}
              >
                How did you hear about us?
              </Typography>
              <TextField
                select
                fullWidth
                value={values.referral}
                onChange={handleChange("referral")}
                displayEmpty
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InfoRoundedIcon sx={{ color: "rgba(43,30,23,0.4)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSX}
              >
                <MenuItem value="" disabled>
                  Select an option
                </MenuItem>
                <MenuItem value="friend">Friend / Family</MenuItem>
                <MenuItem value="therapist">Therapist / Provider</MenuItem>
                <MenuItem value="search">Search Engine</MenuItem>
                <MenuItem value="social">Social Media</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Box>
          </Box>

          {/* PHI-safe/issue-selection field removed per March 11 decision — simplifies booking and avoids HIPAA ambiguity */}

          {/* Consent checkbox */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                bgcolor: "#F3EEE7",
                borderRadius: 2,
                p: 2.5,
                border: "1px solid #D9CEC7",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.consent}
                    onChange={handleCheck}
                    size="medium"
                    sx={{
                      color: "#2B3F53",
                      "&.Mui-checked": { color: "#2B3F53" },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      ...theme.typography.subtext,
                      fontSize: { xs: 14, sm: 15 },
                      lineHeight: 1.6,
                    }}
                  >
                    I understand this is a free, confidential consultation and
                    that my information will be kept private in accordance with
                    HIPAA standards.
                  </Typography>
                }
                sx={{ ml: 0, alignItems: "flex-start" }}
              />
            </Box>
          </Box>

          {/* Submit button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CtaButton
              text="Submit Consultation Request"
              onClick={() => {}}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 320 },
                py: 1.75,
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
